import { deployments } from './env';

export interface DeploymentConfig {
  deploymentName: string;
  model: string;
  systemPrompt: string;
  temperature: number;
  maxTokens: number;
  topP: number;
  responseFormat?: { type: 'json_object' };
  stopSequences?: string[];
}

export interface ModelConfigs {
  [key: string]: DeploymentConfig;
}

/**
 * Configuration for Azure OpenAI deployments
 * Byt här om distributionens namn ändras i Azure - använd deployments från env.ts
 * 
 * NOTE: Token limits varierar per use case:
 * - Fältmappning: 2048 tokens (snabb validering)
 * - Revision: 8000 tokens (omfattande analys av stora dataset)
 * 
 * Detta ger bättre throughput (48 req/min vs 12 req/min) för enkla operationer
 * samtidigt som revision får tillräckligt med output för kompletta analyser.
 */
export const modelConfigs: ModelConfigs = {
  'gpt-5-mini': {
    deploymentName: deployments.mini,
    model: deployments.mini,
    systemPrompt: 'Du är en strikt revisionsassistent. Svara endast i JSON enligt angivet schema. Ange alltid confidence [0,1]. Om osäker: sätt needs_human_review=true och ge kort reasoning. Maskera PII. Var deterministisk.',
    temperature: 0.0,
    maxTokens: 16000, // Ökat från 1500 → 16000 för stora dataset
    topP: 1.0,
    responseFormat: { type: 'json_object' },
    stopSequences: ['\n\n', '```']
  },
  'gpt-4o-mini': {
    deploymentName: deployments.deterministicMini,
    model: 'gpt-4o-mini',
    systemPrompt: 'Du är en strikt revisionsassistent. Svara endast i JSON enligt angivet schema. Ange alltid confidence [0,1]. Om osäker: sätt needs_human_review=true och ge kort reasoning. Maskera PII. Var deterministisk.',
    temperature: 0.0,  // Deterministisk analys
    maxTokens: 16000,
    topP: 1.0,
    responseFormat: { type: 'json_object' },
    stopSequences: ['\n\n', '```']
  },
  'gpt-4.1-premium': {
    deploymentName: deployments.oldPremium,
    model: deployments.oldPremium,
    systemPrompt: `Du är en professionell rapportskrivare för revision. Skriv klar, saklig svenska för ledning och revisorer. 
    
VIKTIGA INSTRUKTIONER:
- Gör inga absoluta påståenden utan datapunkt
- Håll dig inom ordgränser jag anger
- Avslöja inte metodik
- Om det finns exkluderade moduler, inkludera en sektion "Ej genomförda moduler" i slutet av rapporten med följande format:

## Ej genomförda moduler

Följande moduler exkluderades från revisionen på uppdrag av kunden:

• [Modulnamn 1]
• [Modulnamn 2]

Observera att dessa områden inte har granskats och ingår därför inte i revisorns bedömning.`,
    temperature: 0.5,
    maxTokens: 16000, // Ökat från 2000 → 16000 för stora rapporter
    topP: 1.0
  },
  'gpt-5-premium': {
    deploymentName: deployments.premium,
    model: deployments.premium,
    systemPrompt: `Du är en professionell rapportskrivare för revision. Skriv klar, saklig svenska för ledning och revisorer. 
    
VIKTIGA INSTRUKTIONER:
- Gör inga absoluta påståenden utan datapunkt
- Håll dig inom ordgränser jag anger
- Avslöja inte metodik
- Om det finns exkluderade moduler, inkludera en sektion "Ej genomförda moduler" i slutet av rapporten med följande format:

## Ej genomförda moduler

Följande moduler exkluderades från revisionen på uppdrag av kunden:

• [Modulnamn 1]
• [Modulnamn 2]

Observera att dessa områden inte har granskats och ingår därför inte i revisorns bedömning.`,
    temperature: 0.5,
    maxTokens: 16000, // Ökat från 2000 → 16000 för stora rapporter
    topP: 1.0
  },
  'gpt-5-support': {
    deploymentName: deployments.support,
    model: deployments.support,
    systemPrompt: 'Du är en hjälpsam support-assistent för revisionsapplikationen. Svara på svenska och var professionell men vänlig. Hjälp användare med frågor om appen, dess funktioner, och teknisk support.',
    temperature: 0.7,
    maxTokens: 16000, // Ökat från 1500 → 16000 för konsistens
    topP: 1.0
  }
};

/**
 * NOTE: Azure OpenAI client creation is no longer needed in frontend code.
 * All Azure OpenAI calls are now made via edge functions for better security.
 * 
 * The modelConfigs above are kept for reference and configuration purposes,
 * but actual API calls should use the edge functions:
 * - supabase/functions/azure-openai-mini
 * - supabase/functions/azure-openai-premium
 */